import { capitalize, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {
  ContentEmpty,
  IconName,
  SideDrawer,
  Organization,
  Icon,
  OrganizationMembership,
} from "@inductiveautomation/ws-components";

import {
  OrganizationActionCallback,
  OrganizationActionType,
} from "./Organizations.types";
import { membershipKind, stateForMembershipStatus } from "./Organizations.data";
import i18n from "../../i18n/i18n";
import { TranslateKey } from "../../i18n/i18n.types";
import { actionsForMembership } from "./Organizations.actions";
import { testIdForOrganizationDrawerAction } from "./Organizations.locators";

const iconForStatus: Record<string, IconName> = {
  AVAILABLE: "person-add",
  PENDING: "person-pending",
  INVITED: "person-pending",
  JOINED: "person",
};

const headingForStatus: Record<
  OrganizationMembership["membershipStatus"],
  TranslateKey
> = {
  AVAILABLE: "organizations.status.available.heading",
  PENDING: "organizations.status.pending.heading",
  INVITED: "organizations.status.invited.heading",
  JOINED: "organizations.status.joined.heading",
};

const getDescriptionForStatus = (
  t: TFunction,
  membership: OrganizationMembership
): string => {
  return t(
    `organizations.status.${toLower(membership.membershipStatus)}.description`,
    {
      membership: toLower(membershipKind(membership)),
      email: membership.membershipEmail,
    }
  );
};

const createActions = (
  types: Exclude<OrganizationActionType, "leave_multiple_organizations">[],
  organization: Pick<Organization, "orgId" | "orgName">,
  onClick: OrganizationActionCallback
) =>
  types.map((type) => ({
    id: type,
    testId: testIdForOrganizationDrawerAction(organization, type),
    label: i18n.t(`organizations.actions.${type}.button_label`),
    icon: type === "launch_workspace" ? <Icon name="open" /> : undefined,
    onClick: () => onClick(type, [organization.orgId]),
  }));

const actionsForStatus: Record<
  OrganizationMembership["membershipStatus"],
  Exclude<OrganizationActionType, "leave_multiple_organizations">[]
> = {
  AVAILABLE: ["request_to_join"],
  INVITED: ["accept_invite"],
  PENDING: ["cancel_request", "resend_request"],
  JOINED: ["launch_workspace"],
};

const OrganizationsDrawerContents = ({
  membership,
  onClick,
}: {
  membership: OrganizationMembership;
  onClick: OrganizationActionCallback;
}) => {
  const { t } = useTranslation();

  const status = membership.membershipStatus;
  const actions = createActions(
    actionsForStatus[status],
    membership.organization,
    onClick
  );

  return (
    <ContentEmpty
      icon={iconForStatus[status]}
      iconAvatarColor={status === "JOINED" ? "primary" : "empty"}
      heading={t(headingForStatus[status])}
      description={getDescriptionForStatus(t, membership)}
      actions={actions}
    />
  );
};

const OrganizationsDrawer = ({
  selectedMembership,
  open,
  onAction,
  onClose,
}: {
  selectedMembership?: OrganizationMembership;
  open: boolean;
  onAction: OrganizationActionCallback;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  const status = selectedMembership?.membershipStatus;

  return (
    <SideDrawer
      testId="organization-drawer"
      open={open}
      onClose={onClose}
      variant="modal"
      hideBackdrop
      headerProps={{
        icon: "organization",
        avatarProps: {
          iconAvatarColor: "primary",
        },
        heading: selectedMembership?.organization.orgName ?? "",
        overline: t("organizations.singular_item_name"),
        showCloseButton: true,
      }}
      footerProps={{
        actionsLabel: "Actions",
        actions: status
          ? [actionsForMembership(selectedMembership, onAction)]
          : [],
        chip: {
          id: "organization-drawer-chip",
          label: capitalize(selectedMembership?.membershipStatus),
          state: status ? stateForMembershipStatus[status] : undefined,
          variant: "status",
        },
      }}
    >
      {status ? (
        <OrganizationsDrawerContents
          membership={selectedMembership}
          onClick={onAction}
        />
      ) : null}
    </SideDrawer>
  );
};

export default OrganizationsDrawer;
